/**
 * SourceStrip — Horizontal row of source favicons + domains
 *
 * Perplexity-style source strip: shows favicons, domains, and source count.
 * Clickable — opens source URL in a new tab.
 */
import React, { useState } from 'react';
import type { SourceRef, SourceStripData } from '../types';
import { ChevronRight } from '../../shared';

function faviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`;
}

interface SourceStripProps {
  data: SourceStripData;
}

export const SourceStrip: React.FC<SourceStripProps> = ({ data }) => {
  const { sources, maxVisible = 6 } = data;
  const [expanded, setExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  const visible = expanded ? sources : sources.slice(0, maxVisible);
  const hiddenCount = sources.length - maxVisible;

  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      {visible.map((source: SourceRef) => (
        <a
          key={source.id}
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          title={source.title || source.domain}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-cyan-400/5 border border-cyan-400/20 hover:border-cyan-400/50 hover:bg-cyan-400/10 transition-all duration-200 group"
        >
          <img
            src={source.favicon || faviconUrl(source.domain)}
            alt={source.domain}
            className="w-4 h-4 rounded-sm"
            onError={(e) => {
              (e.target as HTMLImageElement).src = faviconUrl(source.domain);
            }}
          />
          <span className="text-xs text-cyan-400/70 group-hover:text-cyan-300 font-medium truncate max-w-[100px]">
            {source.domain}
          </span>
        </a>
      ))}

      {!expanded && hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-cyan-400/5 border border-cyan-400/20 hover:border-cyan-400/40 hover:bg-cyan-400/10 transition-all duration-200 text-xs text-cyan-400/60 hover:text-cyan-300"
        >
          +{hiddenCount} more
          <ChevronRight className="w-3 h-3" />
        </button>
      )}

      {/* Source count badge */}
      <span className="ml-auto text-[10px] text-cyan-400/40 font-bold uppercase tracking-wider">
        {sources.length} source{sources.length !== 1 ? 's' : ''}
      </span>
    </div>
  );
};

export default SourceStrip;
